import { forwardRef } from 'react';

import type {
  IButtonProps,
  TLoadingColor
} from '@/modules/perseo-billing/ui/react/src/components/beta';
import { Loading } from '@/modules/perseo-billing/ui/react/src/components/beta';

export const CardButton = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      color,
      variant,
      disabled = false,
      fullWidth = false,
      loading = false,
      type = 'button',
      direction = 'row',
      children,
      size = 'medium',
      rounded = 'full',
      ariaLabel,
      ...props
    },
    ref
  ) => {
    const buttonClass = {
      disabledContained:
        'co-bg-disabled-light co-text-disabled-main co-border-disabled-light',
      disabledOutlined:
        'co-bg-transparent co-border-disabled-main co-text-disabled-main co-border-1',
      outlined: `co-bg-transparent co-text-${color}-main co-border-${color}-main co-border-1 focus:co-ring-2 focus:co-ring-blue-300`,
      contained: `co-bg-${color}-main co-text-${color}-text hover:co-bg-${color}-dark co-border-${color}-main focus:co-ring-2 focus:co-ring-blue-300`,
      disabledLoadingOutlined: `co-bg-transparent co-text-${color}-main co-border-${color}-main co-border-1`,
      disabledLoadingContained: `co-bg-${color}-main co-text-${color}-text hover:co-bg-${color}-dark co-border-${color}-main`,
      gray: 'co-bg-gray-xlight co-text-gray-main co-border-gray-xlight',
      pdsErrorContained:
        'co-bg-pds-error-500 co-text-white hover:co-bg-pds-error-600 co-border-pds-error-500',
      pdsErrorOutlined:
        'co-bg-transparent co-text-pds-error-500 co-border-pds-error-500'
    };
    const getButtonClass = (): string => {
      if (disabled && loading) {
        if (variant === 'contained') {
          return buttonClass.disabledLoadingContained;
        }
        return buttonClass.disabledLoadingOutlined;
      }
      if (disabled && variant === 'contained') {
        return buttonClass.disabledContained;
      }

      if (disabled && variant === 'outlined') {
        return buttonClass.disabledOutlined;
      }

      if (variant === 'contained') {
        if (color === 'gray') {
          return buttonClass.gray;
        }
        if (color === 'pds-error') {
          return buttonClass.pdsErrorContained;
        }
        return buttonClass.contained;
      }

      if (variant === 'outlined') {
        if (color === 'gray') {
          return buttonClass.gray;
        }
        if (color === 'pds-error') {
          return buttonClass.pdsErrorOutlined;
        }
        return buttonClass.outlined;
      }

      return '';
    };

    const createSizeButton = (): string => {
      if (size === 'large') return 'co-h-56px co-text-body1';
      if (size === 'medium') return 'co-h-48px co-text-body1';
      return 'co-h-40px co-text-body2';
    };

    const createIconColor = (): TLoadingColor => {
      if (color && variant === 'outlined') return color;
      return !color ? 'black' : 'white';
    };

    return (
      <button
        ref={ref}
        aria-label={ariaLabel}
        type={type}
        disabled={disabled}
        className={`
                ${getButtonClass()}
                ${fullWidth ? 'co-w-full' : 'co-w-auto co-min-w-45'}
                ${createSizeButton()}
                co-flex-${'-' + direction}
                ${
                  disabled || loading
                    ? 'co-pointer-events-none'
                    : 'co-pointer-events-auto'
                }
                co-rounded-${rounded}
                co-flex
                co-items-center
                co-justify-center
                co-gap-2
                co-font-montserrat
                co-font-bold
                co-button-ui
                `}
        {...props}
      >
        {loading ? (
          <Loading color={createIconColor()} variant="dots" />
        ) : (
          children
        )}
      </button>
    );
  }
);
